import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PublicCourse } from '@/types/course';
import { formatPriceSimple } from '@/utils/courseFormat';
import { CourseApi } from '@/services/courseApi';
import CourseComboPrice, { type GiftComboInfo } from '@/components/course/CourseComboPrice';

interface CourseCardProps {
  course: PublicCourse;
  onClick?: (courseId: number) => void;
  className?: string;
  /** Optional preloaded combo (avoids N+1 on listing pages) */
  giftCombo?: GiftComboInfo | null;
}

const METHOD_LABEL: Record<string, string> = {
  VIDEO: 'Video / Self-paced',
  ONLINE: 'Online Live',
  OFFLINE: 'Offline Workshop',
  HYBRID: 'Hybrid',
};

const CourseCard = ({ course, onClick, className = '', giftCombo = null }: CourseCardProps) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [combo, setCombo] = useState<GiftComboInfo | null>(giftCombo);

  useEffect(() => {
    setCombo(giftCombo);
  }, [giftCombo]);

  useEffect(() => {
    if (giftCombo || !course?.id) return;
    let cancelled = false;
    CourseApi.getGiftOffers(course.hashId || course.id)
      .then((offers) => {
        if (cancelled) return;
        const first = offers.asSource?.[0];
        if (!first) {
          setCombo(null);
          return;
        }
        const sourceFee = first.sourceCourseFee ?? course.fee ?? 0;
        const giftFee = first.giftCourseFee ?? 0;
        setCombo({
          sourceCourseId: course.id,
          sourceCourseName: course.name,
          sourceCourseHashId: course.hashId,
          sourceCourseFee: sourceFee,
          giftCourseId: first.giftCourseId,
          giftCourseName: first.giftCourseName,
          giftCourseHashId: first.giftCourseHashId,
          giftCourseFee: giftFee,
          originalFee: sourceFee + giftFee,
          comboFee: sourceFee,
        });
      })
      .catch(() => {
        if (!cancelled) setCombo(null);
      });
    return () => {
      cancelled = true;
    };
  }, [course?.id, course?.hashId, course?.fee, course?.name, giftCombo]);

  const handleClick = () => {
    if (onClick) onClick(course.id);
  };

  const methodKey = (course.method || 'VIDEO').toUpperCase();
  const methodLabel = METHOD_LABEL[methodKey] || methodKey;
  const tags = (course.tagNames || []).slice(0, 3);
  const lecturer =
    course.lecturer?.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim() || 'M2MBA Academy';

  if (!course) {
    return (
      <div className="border border-m2-primary/25 bg-m2-surface-lowest p-4 text-m2-on-surface-variant">
        Hiện tại trung tâm chưa có khóa học nào
      </div>
    );
  }

  return (
    <article
      className={`group flex cursor-pointer flex-col overflow-hidden border border-m2-primary/25 bg-m2-surface-lowest shadow-sm transition-[border-color,box-shadow,transform] duration-200 hover:-translate-y-0.5 hover:border-m2-primary/55 hover:shadow-eh-soft focus-within:border-m2-primary focus-within:ring-2 focus-within:ring-m2-primary/25 ${className}`}
    >
      <Link
        to={`/courses/${course.hashId ?? course.id}`}
        onClick={handleClick}
        className="flex flex-grow flex-col focus-visible:outline-none"
      >
        <div className="relative aspect-[4/3] overflow-hidden border-b border-m2-primary/15 bg-white p-2">
          {course.imageUrl && !imageError ? (
            <img
              src={course.imageUrl}
              alt={course.name}
              className={`h-full w-full object-contain transition-opacity duration-300 ${
                imageLoading ? 'opacity-0' : 'opacity-100'
              }`}
              onLoad={() => setImageLoading(false)}
              onError={() => {
                setImageError(true);
                setImageLoading(false);
              }}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-m2-surface-high">
              <span className="material-symbols-outlined text-5xl text-m2-primary/55">
                play_circle
              </span>
            </div>
          )}
          {imageLoading && course.imageUrl && !imageError && (
            <div className="absolute inset-0 animate-pulse bg-white" />
          )}
        </div>

        <div className="flex flex-grow flex-col p-4">
          <h3 className="eh-heading mb-1 line-clamp-2 text-[1.125rem] text-m2-on-surface transition-colors group-hover:text-m2-primary group-focus-within:text-m2-primary">
            {course.name || 'Unnamed Course'}
          </h3>

          <p className="mb-3 text-sm font-medium text-m2-on-surface-variant">{lecturer}</p>

          <div className="mb-3">
            {combo ? (
              <CourseComboPrice combo={combo} variant="card" />
            ) : course.fee === 0 ? (
              <span className="text-lg font-bold text-m2-primary">Miễn phí</span>
            ) : (
              <span className="text-lg font-bold text-m2-on-surface">
                {formatPriceSimple(course.fee || 0)}
              </span>
            )}
          </div>

          <div className="mb-2 flex flex-wrap gap-1">
            <span className="rounded-eh-pill bg-m2-primary-container px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-m2-on-primary-container">
              {methodLabel}
            </span>
          </div>

          {tags.length > 0 && (
            <div className="mt-auto flex flex-wrap gap-1">
              {tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-eh-pill border border-m2-outline px-2 py-0.5 text-xs font-medium text-m2-on-surface-variant"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </Link>
    </article>
  );
};

export default CourseCard;
